import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, IconButton, Tooltip, Link, Zoom} from 'views/components';
import {ContentCopy as ContentCopyIcon, Edit as EditIcon} from 'views/icons';
import {Translate, translate} from 'views/module';

import EditShortId from './EditShortId';

import simplifyQuestionss from 'utils/user_bio/simplify_questions';

const CaregiverProfileUrl = (props) => {
  const {
    shortIdRecord,
    caregiverBioQuestions,
    caregiverBackend,
  } = props;

  const [isCopying, setIsCopying] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);

  const caregiverDomain = process.env.REACT_APP_CLIENT_DOMAIN;
  const shortId = shortIdRecord.shortId;
  const profileUrl = `${caregiverDomain}/caregivers/${shortId}`;

  const questions = simplifyQuestionss(caregiverBioQuestions.questions);

  const requiredBioFields = [
    'firstName',
    'lastName',
    'city',
    'province',
  ];

  // Compute missing fields
  const missingFields = React.useMemo(() => {
    const fields = requiredBioFields.filter(
        (field) => !questions?.[field] || (typeof questions[field] === 'string' && !questions[field].trim()),
    );
    ['personalBio', 'caregiverMotivation'].forEach((field) => {
      if (!questions?.[field]?.trim()) fields.push(field);
    });
    return fields;
  }, [questions, requiredBioFields]);

  const containsMinData = missingFields.length === 0 ||
    (missingFields.length === 1 && (missingFields.includes('personalBio') || missingFields.includes('caregiverMotivation')));

  // Turn missing fields into a sentence with translations
  const missingFieldsSentence = React.useMemo(() => {
    return missingFields
        .map((field) =>
          translate( `caregiver.CaregiverDetail.Settings.ProfileUrl.RequiredFields.${field}`),
        )
        .join(', ');
  }, [missingFields]);

  const copyProfileUrl = async () => {
    setIsCopying(true);
    try {
      await window.navigator.clipboard.writeText(profileUrl);
    } catch (err) {
      // silently fail for now
    } finally {
      setTimeout(() => {
        setIsCopying(false);
      }, 1000);
    }
  };

  return (
    <Stack
      spacing={2}
      sx={{
      }}
    >
      <Stack>
        {
          containsMinData &&
          <Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                sx={{
                  fontSize: '14px',
                  wordBreak: 'break-all',
                  textAlign: 'left',
                }}
              >
                <Link
                  href={profileUrl}
                  target="_blank"
                  rel="noopener"
                  sx={{
                    fontSize: '14px',
                    wordBreak: 'break-all',
                    textAlign: 'left',
                  }}
                  labelText={profileUrl}
                />
              </Typography>
              <Stack direction="row">
                <Tooltip
                  title={translate('caregiver.CaregiverDetail.Settings.ProfileUrl.Copy')}
                  placement="top"
                  TransitionComponent={Zoom}
                >
                  <IconButton onClick={copyProfileUrl}>
                    <ContentCopyIcon
                      sx={{
                        color: isCopying ? 'secondary.main' : 'inherit',
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={translate( 'caregiver.CaregiverDetail.Settings.ProfileUrl.Edit')}
                  placement="bottom"
                  TransitionComponent={Zoom}
                >
                  <IconButton onClick={() => setIsEditing(true)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
        }
        {
          !containsMinData &&
          <Typography
            sx={{
              fontSize: '14px',
              color: 'error.main',
              textAlign: 'left',
              fontWeight: 500,
            }}
          >
            <Translate
              expression='caregiver.CaregiverDetail.Settings.ProfileUrl.InsufficientBioData'
              substitute={{missingFields: missingFieldsSentence}}
            />
          </Typography>
        }
      </Stack>
      {/* Edit Profile URL Dialog */}
      {
        isEditing &&
        <EditShortId
          shortId={shortId}
          caregiverBackend={caregiverBackend}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
      }
    </Stack>
  );
};

CaregiverProfileUrl.propTypes = {
  caregiverData: PropTypes.object.isRequired,
  shortIdRecord: PropTypes.object.isRequired,
  caregiverBackend: PropTypes.object.isRequired,
  caregiverBioQuestions: PropTypes.object.isRequired,
};

export default React.memo(CaregiverProfileUrl);
